import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { tieneCoordenadasValidas } from '../utils/coordenadas';
import { clientesService } from '../services/api';
import RequireRole from './RequireRole';

const PanelContainer = styled.div`
  width: ${props => props.collapsed ? '52px' : '350px'};
  background: ${props => props.collapsed ? 'transparent' : '#f8f9fa'};
  border-right: ${props => props.collapsed ? 'none' : '1px solid #dee2e6'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.3s ease;
  position: relative;
  z-index: 10;
`;

const CollapseButton = styled.button`
  padding: 6px 12px;
  background: #343a40;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  transition: background 0.2s ease;

  &:hover {
    background: #212529;
  }
`;

const CollapsedContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 0.5rem;
  padding-left: 20px;
`;

const PanelContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const VendedorItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const VendedorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ColorIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color || '#666'};
  border: 2px solid white;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
`;

const VendedorName = styled.span`
  font-weight: 500;
  color: #2c3e50;
`;

const ClienteCount = styled.span`
  background: #e9ecef;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SinCoordenadasCount = styled.span`
  color: #dc3545;
  font-size: 0.75rem;
  font-weight: bold;
`;

const TagBadge = ({ children, count, color = '#6c757d' }) => (
  <div style={{
    backgroundColor: '#e9ecef',
    padding: '0.25rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem'
  }}>
    <span>{children}</span>
    <span style={{
      backgroundColor: color,
      color: 'white',
      borderRadius: '50%',
      width: '18px',
      height: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.7rem'
    }}>
      {count}
    </span>
  </div>
);

const SelectionSection = styled.div`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
`;

const SelectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #2c3e50;
`;

const SelectionInfo = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  font-weight: 500;
  color: #2c3e50;
`;

const ReasignacionSection = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: ${props => props.disabled ? '#6c757d' : '#28a745'};
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 1rem;
  
  &:hover {
    background: ${props => props.disabled ? '#6c757d' : '#218838'};
  }
`;

const selectStyles = {
  container: (provided) => ({
    ...provided,
    marginBottom: '1rem',
  }),
  control: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
};

// Área scrollable para el listado de vendedores
const VendedoresScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

// Contenedor fijo para la sección de selección
const SelectionWrapper = styled.div`
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
`;

const SidePanel = ({ 
  vendedores, 
  clientes, 
  selectedClientes, 
  onReasignarClientes, 
  loading,
  onReload,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [nuevoVendedor, setNuevoVendedor] = useState(null);
  const [notas, setNotas] = useState('');

  // Calcular estadísticas de vendedores
  const vendedorStats = useMemo(() => {
    const stats = {};
    
    vendedores.forEach(vendedor => {
      const clientesVendedor = clientes.filter(c => c.clt_ven_codigo === vendedor.ven_codigo);
      const clientesSinCoordenadas = clientesVendedor.filter(c => !tieneCoordenadasValidas(c)).length;
      
      stats[vendedor.ven_codigo] = {
        ...vendedor,
        clienteCount: clientesVendedor.length,
        clientesSinCoordenadasCount: clientesSinCoordenadas
      };
    });
    
    return Object.values(stats);
  }, [vendedores, clientes]);

  // Información del vendedor actual de los clientes seleccionados
  const vendedorActual = useMemo(() => {
    if (selectedClientes.length === 0) return null;
    
    const vendedorIds = [...new Set(selectedClientes.map(c => c.clt_ven_codigo))];
    
    if (vendedorIds.length === 1) {
      return vendedores.find(v => v.ven_codigo === vendedorIds[0]);
    }
    
    return { ven_nombre: 'Múltiples vendedores' };
  }, [selectedClientes, vendedores]);
  
  // Agrupar clientes por día de visita
  const [visitasPorDia, frecuencias, semanasVisita] = useMemo(() => {
    const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const conteoDias = {};
    const conteoFrecuencias = {
      'SEMANAL': 0,
      'QUINCENAL': 0,
      'MENSUAL': 0
    };
    const conteoSemanas = {};
    
    // Inicializar contadores de días
    dias.forEach(dia => {
      conteoDias[dia] = 0;
    });
    
    // Contar clientes por día, frecuencia y semana
    selectedClientes.forEach(cliente => {
      // Contar por día
      if (cliente.clt_diavisita && dias.includes(cliente.clt_diavisita)) {
        conteoDias[cliente.clt_diavisita]++;
      }
      
      // Contar por frecuencia
      if (cliente.clt_frecuencia && conteoFrecuencias.hasOwnProperty(cliente.clt_frecuencia)) {
        conteoFrecuencias[cliente.clt_frecuencia]++;
      }
      
      // Contar por semana de visita (solo si tiene valor)
      if (cliente.clt_semanavisita) {
        conteoSemanas[cliente.clt_semanavisita] = (conteoSemanas[cliente.clt_semanavisita] || 0) + 1;
      }
    });
    
    // Filtrar y formatear resultados
    const diasFiltrados = Object.entries(conteoDias)
      .filter(([_, count]) => count > 0)
      .map(([dia, count]) => ({ dia, count }));
      
    const frecuenciasFiltradas = Object.entries(conteoFrecuencias)
      .filter(([_, count]) => count > 0)
      .map(([frecuencia, count]) => ({
        frecuencia,
        label: frecuencia.charAt(0) + frecuencia.slice(1).toLowerCase(),
        count
      }));
      
    const semanasFiltradas = Object.entries(conteoSemanas)
      .filter(([_, count]) => count > 0)
      .map(([semana, count]) => ({
        semana,
        count
      }));
    
    return [diasFiltrados, frecuenciasFiltradas, semanasFiltradas];
  }, [selectedClientes]);

  // Opciones para el select de nuevo vendedor
  const vendedorOptions = vendedores.map(vendedor => ({
    value: vendedor.ven_codigo,
    label: vendedor.ven_codigo + ' - ' + vendedor.ven_nombre,
    color: vendedor.ven_color
  }));

  const handleReasignar = async () => {
    if (!nuevoVendedor || selectedClientes.length === 0) {
      toast.error('Selecciona un vendedor y clientes para reasignar');
      return;
    }

    try {
      // Validación cruzada: bloquear si hay Cambios de Visita pendientes
      const resumen = await clientesService.getResumenPendientes();
      if (resumen && Number(resumen.cambios_visita_pendientes) > 0) {
        toast.warning('No puedes reasignar vendedor mientras existan Cambios de Visita pendientes. Finaliza o espera la aprobación.');
        if (onReload) await onReload();
        return;
      }

      const resp = await onReasignarClientes(nuevoVendedor.value, notas);
      setNuevoVendedor(null);
      setNotas('');
      toast.success(`${selectedClientes.length} clientes reasignados exitosamente`);
      if (resp && resp.lote_id) {
        toast.info(`Solicitud registrada. Lote #${resp.lote_id}`);
      }
    } catch (error) {
      toast.error('Error al reasignar clientes');
    }
  };

  if (isCollapsed) {
    return (
      <PanelContainer collapsed>
        <CollapsedContent>
          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: '#343a40', fontWeight: 600 }}>
            Panel de vendedores
          </span>
          <CollapseButton
            type="button"
            onClick={onToggleCollapse}
            aria-label="Mostrar panel lateral"
            aria-expanded={false}
            compact
          >
            ⮜ Abrir
          </CollapseButton>
        </CollapsedContent>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer>
      <PanelContent>
        <VendedoresScroll>
          {/* Resumen de vendedores */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>
              Vendedores (Total clientes)
            </h3>
            <CollapseButton
              type="button"
              onClick={onToggleCollapse}
              aria-label="Ocultar panel lateral"
              aria-expanded={true}
            >
              ⮞ Ocultar
            </CollapseButton>
          </div>

          {vendedorStats
            .filter(vendedor => vendedor.clienteCount > 0)
            .map(vendedor => (
              <VendedorItem key={vendedor.ven_codigo}>
                <VendedorInfo>
                  <ColorIndicator color={vendedor.ven_color} />
                  <VendedorName>{vendedor.ven_codigo + ' - ' + vendedor.ven_nombre}</VendedorName>
                </VendedorInfo>
                <ClienteCount>
                  ({vendedor.clienteCount})
                  {vendedor.clientesSinCoordenadasCount > 0 && (
                    <SinCoordenadasCount>
                      {vendedor.clientesSinCoordenadasCount}
                    </SinCoordenadasCount>
                  )}
                </ClienteCount>
              </VendedorItem>
            ))}
        </VendedoresScroll>

        <SelectionWrapper>
          {selectedClientes.length > 0 ? (
            <SelectionSection>
              <SelectionTitle>Clientes Seleccionados</SelectionTitle>
              
              <SelectionInfo>
                <InfoRow>
                  <InfoLabel>Clientes seleccionados:</InfoLabel>
                  <InfoValue>{selectedClientes.length}</InfoValue>
                </InfoRow>
                
                {vendedorActual && (
                  <InfoRow>
                    <InfoLabel>Vendedor actual:</InfoLabel>
                    <InfoValue>
                      <ColorIndicator 
                        color={vendedorActual.ven_color} 
                        style={{ display: 'inline-block', marginRight: '0.5rem' }}
                      />
                      {vendedorActual.ven_nombre}
                    </InfoValue>
                  </InfoRow>
                )}
                
                <div style={{ marginTop: '0.5rem' }}>
                  {/* Días de visita */}
                  {visitasPorDia.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <InfoLabel style={{ display: 'block', marginBottom: '0.25rem' }}>
                        Días de visita:
                      </InfoLabel>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {visitasPorDia.map(({ dia, count }) => (
                          <TagBadge key={`dia-${dia}`} count={count}>
                            {dia}
                          </TagBadge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Frecuencias */}
                  {frecuencias.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <InfoLabel style={{ display: 'block', marginBottom: '0.25rem' }}>
                        Frecuencias:
                      </InfoLabel>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {frecuencias.map(({ label, count }) => (
                          <TagBadge key={`freq-${label}`} count={count} color="#0d6efd">
                            {label}
                          </TagBadge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Semanas de visita */}
                  {semanasVisita.length > 0 && (
                    <div>
                      <InfoLabel style={{ display: 'block', marginBottom: '0.25rem' }}>
                        Semanas de visita:
                      </InfoLabel>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {semanasVisita.map(({ semana, count }) => (
                          <TagBadge key={`sem-${semana}`} count={count} color="#198754">
                            {semana}
                          </TagBadge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </SelectionInfo>

              <RequireRole 
                roles={['admin','gerente', 'supervisor']}
                fallback={
                  <SelectionInfo>
                    <InfoValue style={{ color: '#6c757d', textAlign: 'center', fontStyle: 'italic' }}>
                      No tienes permisos para reasignar clientes
                    </InfoValue>
                  </SelectionInfo>
                }
              >
                <ReasignacionSection>
                  <InfoLabel style={{ display: 'block', marginBottom: '0.5rem' }}>
                    Nuevo vendedor:
                  </InfoLabel>
                  
                  <Select
                    styles={selectStyles}
                    options={vendedorOptions}
                    value={nuevoVendedor}
                    onChange={setNuevoVendedor}
                    menuPlacement="top"
                    placeholder="Seleccionar vendedor"
                    formatOptionLabel={(option) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ColorIndicator color={option.color} />
                        {option.label}
                      </div>
                    )}
                  />
                  
                  <InfoLabel style={{ display: 'block', margin: '0.5rem 0' }}>
                    Notas (opcional):
                  </InfoLabel>
                  <textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Agregar una nota para la solicitud (opcional)"
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                      resize: 'vertical'
                    }}
                  />
                  
                  <ActionButton
                    onClick={handleReasignar}
                    disabled={!nuevoVendedor || loading}
                  >
                    {loading ? 'Guardando...' : 'Guardar cambios'}
                  </ActionButton>
                </ReasignacionSection>
              </RequireRole>
            </SelectionSection>
          ) : (
            <RequireRole 
              roles={['admin','gerente', 'supervisor']}
              fallback={
                <SelectionSection>
                  <SelectionInfo>
                    <InfoValue style={{ color: '#6c757d', textAlign: 'center' }}>
                      Visualiza tus clientes asignados en el mapa
                    </InfoValue>
                  </SelectionInfo>
                </SelectionSection>
              }
            >
              <SelectionSection>
                <SelectionInfo>
                  <InfoValue style={{ color: '#6c757d', textAlign: 'center' }}>
                    Selecciona clientes en el mapa para reasignar vendedor
                  </InfoValue>
                </SelectionInfo>
              </SelectionSection>
            </RequireRole>
          )}
        </SelectionWrapper>
      </PanelContent>
    </PanelContainer>
  );
};

export default SidePanel;
